import { EntityId } from '@reduxjs/toolkit';

import { DocumentContentType, INTRASH, NOTINTRASH, PRIVATE, RECEIVED, SHARED, ALL } from '@helpers';
import { ListOnItemsRenderedProps } from 'react-window';
import { ApolloQueryResult } from '@apollo/client';
import { ListColumnDef } from '@common2/VirtualizedFiles/VirtualizedFilesList/types';

export const LIST = 'list';
export const GRID = 'grid';
export type FilesViewType = typeof LIST | typeof GRID;

export type FileAccessStatus = typeof OWNER | typeof CONTRIBUTOR | typeof EDITOR | typeof VIEWER;

export type FileAccessType =
  | typeof ALL
  | typeof PRIVATE
  | typeof RECEIVED
  | typeof SHARED
  | typeof INTRASH
  | typeof NOTINTRASH;

export type TranscodeStatus = {
  metadata?: string | null;
  thumbnails?: string | null;
  video?: string | null;
};

export const OWNER = 'owner';
export const CONTRIBUTOR = 'contributor';
export const VIEWER = 'viewer';
export const EDITOR = 'editor';

export const ROOT_DIRECTORY = '.';

export interface AccessStatus {
  type: FileAccessStatus;
  level: number;
  label: FileAccessStatus;
  files: File[];
}

export type AcceptableFileExtentions =
  | '.PNG'
  | '.JFIF'
  | '.JPG'
  | '.JPEG'
  | '.AVI'
  | '.WMV'
  | '.MP4'
  | '.MP3'
  | '.MOV'
  | '.FLAC'
  | '.PDF'
  | '.DOCX'
  | '.DOC'
  | '.PPT'
  | '.PPTX'
  | '.TIF'
  | '.TIFF'
  | '.TXT'
  | '.XLS'
  | '.XLSX'
  | '.HTML';

export const MIN_GRID_ITEM_SIZE = 100;
export const MAX_GRID_ITEM_SIZE = 576;

export interface GpraphQLFileContent {
  file_id: string;
  file_type: any;
  content_type: DocumentContentType;
  name: string;
  description: string;
  file_size: number;
  duration: any;
  created: any;
  last_updated: string;
  transcode_status: null | TranscodeStatus;
  transcode_complete: null | boolean;
  parent_dir: {
    name: string;
    file_id: string;
  };
  stream_path: string;
  thumbnails: string;
  comments?: {
    comment_id: string;
  }[];
}

export interface GpraphQLFile {
  access_status: FileAccessStatus;
  access_type: FileAccessType;
  file: GpraphQLFileContent;
}

export interface FileContent extends GpraphQLFileContent {
  url: string;
  thumbnail_url: string;
  access_type: FileAccessType;
  access_status: FileAccessStatus;
}

export interface User {
  given_name?: string;
  family_name?: string;
  email: string;
  state?: string;
  profile_image?: string;
}

export interface UserAccess {
  user_id?: string;
  access_status: FileAccessStatus;
  user: User;
}

export interface FileDetails {
  file_id: string;
  comments: Comment[];
  users: UserAccess[];
  description: string;
}

export interface Comment {
  comment_id: string;
  comment_text: string;
  comment_date: string;
  commentor: Commentor;
  emoticons: Emoticon[];
}

export interface Emoticon {
  id: string;
  emoticon: string;
  commentors: Commentor[];
}

export interface Commentor {
  user_id: string;
  given_name: string;
  family_name: string;
}

export interface File1 extends Omit<GpraphQLFile, 'file'> {
  file: GpraphQLFile['file'] & FileContent;
}

export interface VirtualizedFilesGridProps {
  data: File1[];
  totalItems: number;
  readOnly?: boolean;
  saveScrollPosition?: () => void;
  onFileClick: (file: FileContent) => void;
  setScrollPosition: (newScrollPosition: number) => void;
  onLoadMore: (startIndex: number, stopIndex: number) => Promise<void | ApolloQueryResult<any>>;
}

export interface VirtualizedFilesListProps extends VirtualizedFilesGridProps {
  isDraggable?: boolean;
  columns: ListColumnDef[];
}

export interface VirtualizedGridListItemProps
  extends Omit<VirtualizedFilesGridProps, 'data' | 'onLoadMore'> {
  data: File1[];
  gridItemSize: number;
  height: number;
  width: number;
  onItemsRendered: (props: ListOnItemsRenderedProps) => any; // XZ
}

export interface VirtualizedGridRowItemProps {
  data: {
    files: File1[];
    selectedIds: EntityId[];
    numItemsPerRow: number;
    onSelect: (index: number) => void;
    gridItemSize: number;
    readOnly?: boolean;
    onFileClick: (file: FileContent) => void;
    saveScrollPosition?: () => void;
    lastIndex: number;
  };
  rowIndex: number;
  columnIndex: number;
  style: React.CSSProperties;
}

export interface FileBreadcrumb {
  id: string;
  name: string;
  isFile: boolean;
  accessStatus?: FileAccessStatus;
  isUser?: boolean;
  onClick?: () => void;
}

export interface ResponseParams {
  error: string;
  message: string;
}

export interface FetchFilesResponse extends ResponseParams {
  files?: File[];
  url?: string;
}

interface CreatedDirectory {
  file_id: string;
  user_id: string;
}

export interface CreateDirectoryResponse extends ResponseParams {
  payload: CreatedDirectory[];
}

export interface DownloadIdResponse {
  download_id: string;
}

export interface DownloadUrlResponse {
  status: 'Pending' | 'Ready';
  url?: string;
}

export interface CopyUrlResponse {
  completed: number;
  operation_id: string;
  target_quantity: number;
}

export interface FilesRequestParameters {
  description?: string;
  name?: string;
  dir_id?: string;
}

type FileTypePayload = 'SINGLE_PAYLOAD' | 'EXTENSIVE_PAYLOAD';

export interface ExtensiveFilesRequest<T> {
  files: T;
  parameters: FilesRequestParameters;
}
export interface FilesRequest<T, Payload extends FileTypePayload = 'SINGLE_PAYLOAD'> {
  type?: 'MODIFY' | 'MOVE' | 'COPY' | 'SHARE' | 'SOFT_DELETE' | 'HARD_DELETE' | 'RESTORE';
  payload: Payload extends 'EXTENSIVE_PAYLOAD'
    ? ExtensiveFilesRequest<T>
    : Payload extends 'SINGLE_PAYLOAD'
    ? T
    : never;
}

export interface FilesOperationRequestItem {
  file_id: string;
  share_id?: string;
}

export interface FilesOperationRequest<Payload extends FileTypePayload = 'SINGLE_PAYLOAD'>
  extends FilesRequest<FilesOperationRequestItem[], Payload> {}

export interface ShareFilesRequestItem {
  first_name: string;
  last_name: string;
  target_email: string;
  permission: string;
  shared_files: string[];
}

export interface ShareFilesRequest<Payload extends FileTypePayload = 'SINGLE_PAYLOAD'>
  extends FilesRequest<ShareFilesRequestItem[], Payload> {}

export interface UploadFileRequestItem {
  file_id: string;
  parent_dir_id: string;
  name: string;
  description: string;
  content_type: DocumentContentType | string;
  upload_size?: number;
}

export interface UploadFilesRequest {
  user_id: string;
  files: UploadFileRequestItem[];
}

export interface CommentEmoticonsSet {
  emoticon: string;
  action: 'add' | 'remove';
  id?: string;
}

export interface DeleteUserDataResponse extends ResponseParams {
  users: string[];
}

export type ExportUsersResponse = DownloadIdResponse & ResponseParams;

export interface ExportUsersParams {
  usersIds: EntityId[];
  userRole: string;
}
